// =============================================================================
// FuelLog — shared "Tank Liste".
//
// Usage: people sharing a car each log their odometer reading; whoever
// fills up enters the total cost too. The page splits each fill-up bill
// proportionally to the km each driver drove since the previous fill-up.
//
// This is a fully standalone shared resource (its own invite link), so
// no Calendar wiring is needed.
// =============================================================================
import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Fuel,
  Settings as SettingsIcon,
  X,
  Crown,
  ArrowLeft,
  Copy,
  Trash2,
  Users as UsersIcon,
  LogOut,
  Gauge,
  Receipt
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import { useAuth } from '../lib/AuthContext';
import { useToast } from '../lib/Toast';
import { useConfirm, usePrompt } from '../lib/ConfirmDialog';
import { AVATAR_COLORS } from '../lib/profile';
import {
  listFuelLogs,
  createFuelLog,
  updateFuelLog,
  deleteFuelLog,
  getFuelLogInviteUrl,
  listFuelLogMembers,
  leaveFuelLog,
  listFuelEntries,
  addFuelEntry,
  deleteFuelEntry,
  computeBalances
} from '../lib/fuelLogs';

// -----------------------------------------------------------------------------
// Currency / number formatting helpers. Cached Intl instances per currency.
// -----------------------------------------------------------------------------
const _moneyFormatters = new Map();
const fmtMoney = (n, currency = 'EUR') => {
  if (n == null || !Number.isFinite(n)) return '—';
  let f = _moneyFormatters.get(currency);
  if (!f) {
    try {
      f = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency,
        maximumFractionDigits: 2
      });
    } catch {
      f = new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR',
        maximumFractionDigits: 2
      });
    }
    _moneyFormatters.set(currency, f);
  }
  return f.format(n);
};
const fmtKm = (n) =>
  Number.isFinite(n) ? `${new Intl.NumberFormat('de-DE').format(Math.round(n))} km` : '—';

// -----------------------------------------------------------------------------
// Avatar (mirrors the convention used elsewhere in the app).
// -----------------------------------------------------------------------------
const MemberAvatar = ({ member, size = 28 }) => {
  if (!member) {
    return (
      <div
        className="rounded-full bg-gray-200 dark:bg-gray-700"
        style={{ width: size, height: size }}
      />
    );
  }
  const color =
    AVATAR_COLORS.find((c) => c.id === member.avatarColor) || AVATAR_COLORS[0];
  const initial = (member.displayName || member.email || '?')
    .trim()
    .charAt(0)
    .toUpperCase();
  if (member.avatarKind === 'photo' && member.avatarUrl) {
    return (
      <img
        src={member.avatarUrl}
        alt=""
        className="rounded-full object-cover shadow-sm flex-shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-semibold shadow-sm flex-shrink-0"
      style={{
        width: size,
        height: size,
        backgroundColor: color.swatch,
        fontSize: Math.max(10, Math.round(size * 0.42))
      }}
    >
      {member.avatarEmoji ? (
        <span style={{ fontSize: Math.round(size * 0.55), lineHeight: 1 }}>
          {member.avatarEmoji}
        </span>
      ) : (
        initial
      )}
    </div>
  );
};

// =============================================================================
// FuelLogList — picks which log to view, or shows the empty/create UI.
// =============================================================================
const FuelLog = () => {
  const { user } = useAuth();
  const toast = useToast();
  const prompt = usePrompt();
  const confirm = useConfirm();

  const [logs, setLogs] = useState(null); // null = loading, [] = empty
  const [activeLogId, setActiveLogId] = useState(null);

  // Initial load (and refresh whenever signed-in user changes).
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    listFuelLogs()
      .then((rows) => {
        if (cancelled) return;
        setLogs(rows);
        // Auto-open if there's exactly one log; this is the most common
        // case (a couple sharing a single car).
        if (rows.length === 1) setActiveLogId(rows[0].id);
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err.message || 'Konnte Tank-Listen nicht laden');
        setLogs([]);
      });
    return () => {
      cancelled = true;
    };
    // toast intentionally excluded; it's stable per provider
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleCreate = async () => {
    const name = await prompt({
      title: 'Neue Tank-Liste',
      message: 'Wie soll die Liste heißen? (z.B. „Auto Jana & Gerhard")',
      placeholder: 'Tank-Liste',
      confirmLabel: 'Anlegen'
    });
    if (!name) return;
    try {
      const created = await createFuelLog(name.trim(), 'EUR');
      const refreshed = await listFuelLogs();
      setLogs(refreshed);
      setActiveLogId(created.id);
      toast.success('Tank-Liste angelegt');
    } catch (err) {
      toast.error(err.message || 'Anlegen fehlgeschlagen');
    }
  };

  if (!user) {
    return (
      <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
        Bitte melde dich an, um Tank-Listen zu nutzen.
      </div>
    );
  }

  if (logs === null) {
    return (
      <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
        Lade…
      </div>
    );
  }

  // Active-log view
  if (activeLogId) {
    const activeLog = logs.find((l) => l.id === activeLogId);
    if (!activeLog) {
      // Was deleted / left — fall through to picker.
      setActiveLogId(null);
      return null;
    }
    return (
      <FuelLogDetail
        log={activeLog}
        onBack={logs.length > 1 ? () => setActiveLogId(null) : null}
        onLogChanged={async () => {
          const refreshed = await listFuelLogs();
          setLogs(refreshed);
          if (!refreshed.some((l) => l.id === activeLogId)) {
            setActiveLogId(null);
          }
        }}
      />
    );
  }

  // List / picker view
  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 pb-24 sm:pb-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Fuel size={24} className="text-primary-600" />
            Tank-Listen
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Geteilte Listen für gemeinsame Autos.
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="btn bg-primary-600 text-white hover:bg-primary-700 flex items-center gap-2"
        >
          <Plus size={16} />
          <span className="hidden sm:inline">Neu</span>
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-8 text-center">
          <Fuel size={40} className="mx-auto text-gray-400 mb-3" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Noch keine Tank-Liste
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">
            Erstelle eine neue Liste und schicke der Person, mit der du das
            Auto teilst, den Einladungs-Link.
          </p>
          <button
            onClick={handleCreate}
            className="mt-4 btn bg-primary-600 text-white hover:bg-primary-700"
          >
            Tank-Liste anlegen
          </button>
        </div>
      ) : (
        <ul className="space-y-2">
          {logs.map((l) => (
            <li key={l.id}>
              <button
                onClick={() => setActiveLogId(l.id)}
                className="w-full flex items-center gap-3 p-3 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 hover:border-primary-300 dark:hover:border-primary-700 transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-full bg-primary-50 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-300">
                  <Fuel size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                    {l.name}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {l.isOwner ? 'Inhaber' : 'Mitglied'} · {l.currency}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// =============================================================================
// FuelLogDetail — the actual log: balance card, entries list, add button.
// =============================================================================
const FuelLogDetail = ({ log, onBack, onLogChanged }) => {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();

  const [entries, setEntries] = useState(null); // null = loading
  const [members, setMembers] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const reload = async () => {
    try {
      const [es, ms] = await Promise.all([
        listFuelEntries(log.id),
        listFuelLogMembers(log.id)
      ]);
      setEntries(es);
      setMembers(ms);
    } catch (err) {
      toast.error(err.message || 'Laden fehlgeschlagen');
      setEntries([]);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [log.id]);

  const memberById = useMemo(() => {
    const m = {};
    for (const x of members) m[x.userId] = x;
    return m;
  }, [members]);

  const balances = useMemo(
    () => (entries ? computeBalances(entries) : null),
    [entries]
  );

  const reverseSorted = useMemo(() => {
    if (!entries) return [];
    return [...entries].sort((a, b) => b.odometer - a.odometer);
  }, [entries]);

  // Find previous entry's odometer (in odometer-asc order) so we can show
  // "+X km since last reading" per row.
  const previousByEntryId = useMemo(() => {
    const map = {};
    if (!entries) return map;
    const asc = [...entries].sort((a, b) => a.odometer - b.odometer);
    for (let i = 1; i < asc.length; i++) {
      map[asc[i].id] = asc[i - 1].odometer;
    }
    return map;
  }, [entries]);

  // ---------------------------------------------------------------------------
  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6 pb-24 sm:pb-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        {onBack && (
          <button
            onClick={onBack}
            className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
            aria-label="Zurück"
          >
            <ArrowLeft size={18} />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 truncate flex items-center gap-2">
            <Fuel size={20} className="text-primary-600 flex-shrink-0" />
            {log.name}
          </h1>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {members.length} Mitglied{members.length === 1 ? '' : 'er'}
          </div>
        </div>
        <button
          onClick={() => setShowSettings(true)}
          className="btn-secondary !p-2"
          aria-label="Einstellungen"
        >
          <SettingsIcon size={16} />
        </button>
      </div>

      {/* Balance summary card */}
      {entries === null ? (
        <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-4 text-sm text-gray-500 dark:text-gray-400">
          Lade…
        </div>
      ) : (
        <BalanceCard
          balances={balances}
          memberById={memberById}
          currency={log.currency}
        />
      )}

      {/* Add-entry button */}
      <button
        onClick={() => setShowAdd(true)}
        className="w-full mb-4 btn bg-primary-600 text-white hover:bg-primary-700 flex items-center justify-center gap-2"
      >
        <Plus size={16} />
        Neuer Eintrag
      </button>

      {/* Entries list */}
      {entries !== null && entries.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-6 text-center">
          <Gauge size={32} className="mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Noch keine Einträge. Trage deinen aktuellen Kilometerstand ein,
            um anzufangen.
          </p>
        </div>
      )}

      {entries && entries.length > 0 && (
        <ul className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 divide-y divide-gray-100 dark:divide-gray-800 overflow-hidden">
          {reverseSorted.map((e) => {
            const author = memberById[e.userId];
            const prevOdo = previousByEntryId[e.id];
            const driven =
              prevOdo == null ? null : Math.max(0, e.odometer - prevOdo);
            const isFillUp = e.fuelCost != null && e.fuelCost > 0;
            const isMine = user && e.userId === user.id;
            return (
              <li
                key={e.id}
                className="flex items-start gap-3 p-3"
              >
                <MemberAvatar member={author} size={32} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {author?.displayName || 'Unbekannt'}
                      {isMine && (
                        <span className="ml-1.5 text-xs text-gray-400">
                          (du)
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                      {(() => {
                        try {
                          return format(parseISO(e.entryDate), 'd. MMM yyyy', {
                            locale: de
                          });
                        } catch {
                          return e.entryDate;
                        }
                      })()}
                    </div>
                  </div>
                  <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-sm">
                    <span className="inline-flex items-center gap-1 text-gray-700 dark:text-gray-200">
                      <Gauge size={13} className="text-gray-400" />
                      {fmtKm(e.odometer)}
                    </span>
                    {driven != null && driven > 0 && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        +{fmtKm(driven)}
                      </span>
                    )}
                    {isFillUp && (
                      <span className="inline-flex items-center gap-1 text-primary-700 dark:text-primary-300 font-medium">
                        <Receipt size={13} />
                        {fmtMoney(e.fuelCost, log.currency)}
                        {e.liters != null && (
                          <span className="text-xs font-normal text-gray-500 dark:text-gray-400 ml-0.5">
                            · {e.liters.toFixed(2)} L
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  {e.note && (
                    <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                      {e.note}
                    </div>
                  )}
                </div>
                {(isMine || log.isOwner) && (
                  <button
                    onClick={async () => {
                      const ok = await confirm({
                        title: 'Eintrag löschen?',
                        message:
                          'Das ändert die Abrechnung rückwirkend. Wirklich löschen?',
                        confirmLabel: 'Löschen',
                        destructive: true
                      });
                      if (!ok) return;
                      try {
                        await deleteFuelEntry(e.id);
                        await reload();
                      } catch (err) {
                        toast.error(err.message || 'Löschen fehlgeschlagen');
                      }
                    }}
                    className="p-1 rounded text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0"
                    aria-label="Eintrag löschen"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* Add-entry modal */}
      {showAdd && (
        <AddEntryModal
          log={log}
          lastOdometer={
            entries && entries.length > 0
              ? entries[entries.length - 1].odometer
              : null
          }
          onClose={() => setShowAdd(false)}
          onSaved={async () => {
            setShowAdd(false);
            await reload();
            toast.success('Eintrag gespeichert');
          }}
        />
      )}

      {/* Settings modal */}
      {showSettings && (
        <SettingsModal
          log={log}
          members={members}
          onClose={() => setShowSettings(false)}
          onChanged={async () => {
            await reload();
            await onLogChanged();
          }}
          onLeftOrDeleted={async () => {
            setShowSettings(false);
            await onLogChanged();
          }}
        />
      )}
    </div>
  );
};

// =============================================================================
// BalanceCard — top-of-page summary.
// =============================================================================
const BalanceCard = ({ balances, memberById, currency }) => {
  if (!balances) return null;
  const { perUser, pairwise, fillUpCount, totalKm, unsettledKm } = balances;
  const userIds = Object.keys(perUser);

  if (userIds.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-4 bg-white dark:bg-gray-900">
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Noch keine Daten. Trage einen ersten Kilometerstand ein.
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 p-4 mb-4 bg-gradient-to-br from-primary-50 to-white dark:from-primary-900/20 dark:to-gray-900">
      <div className="text-xs uppercase tracking-wide font-medium text-gray-500 dark:text-gray-400 mb-2">
        Aktueller Stand
      </div>

      {pairwise.length === 0 ? (
        <div className="text-base font-medium text-gray-700 dark:text-gray-200">
          {fillUpCount === 0
            ? 'Noch nicht getankt — keine Abrechnung nötig.'
            : 'Alles ausgeglichen 🎉'}
        </div>
      ) : (
        <ul className="space-y-1.5">
          {pairwise.map((p, i) => {
            const from = memberById[p.from];
            const to = memberById[p.to];
            return (
              <li
                key={i}
                className="flex items-center gap-2 text-sm"
              >
                <MemberAvatar member={from} size={24} />
                <span className="text-gray-700 dark:text-gray-200">
                  <span className="font-medium">
                    {from?.displayName || 'Jemand'}
                  </span>{' '}
                  schuldet{' '}
                </span>
                <MemberAvatar member={to} size={24} />
                <span className="font-medium text-gray-900 dark:text-gray-100">
                  {to?.displayName || 'Jemand'}
                </span>
                <span className="ml-auto font-semibold text-primary-700 dark:text-primary-300">
                  {fmtMoney(p.amount, currency)}
                </span>
              </li>
            );
          })}
        </ul>
      )}

      {/* Per-user km / paid breakdown */}
      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
        <ul className="space-y-1 text-xs text-gray-500 dark:text-gray-400">
          {userIds.map((uid) => {
            const m = memberById[uid];
            const u = perUser[uid];
            return (
              <li key={uid} className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 min-w-0">
                  <MemberAvatar member={m} size={16} />
                  <span className="truncate">
                    {m?.displayName || uid.slice(0, 6)}
                  </span>
                </span>
                <span className="flex-shrink-0 ml-2">
                  {fmtKm(u.km)}
                  {u.paid > 0 && (
                    <span className="ml-2">
                      bezahlt {fmtMoney(u.paid, currency)}
                    </span>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
        {unsettledKm > 0 && (
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {fmtKm(unsettledKm)} seit dem letzten Tanken — wird beim
            nächsten Tanken abgerechnet.
          </div>
        )}
        <div className="mt-1 text-xs text-gray-400 dark:text-gray-500">
          Insgesamt {fmtKm(totalKm)} · {fillUpCount} Tank-Vorgang
          {fillUpCount === 1 ? '' : 'e'}
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// AddEntryModal — date / odometer / optional fuel cost.
// =============================================================================
const AddEntryModal = ({ log, lastOdometer, onClose, onSaved }) => {
  const toast = useToast();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate] = useState(today);
  const [odometer, setOdometer] = useState('');
  const [isFillUp, setIsFillUp] = useState(false);
  const [fuelCost, setFuelCost] = useState('');
  const [liters, setLiters] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const odoNum = Number(odometer);
  const odoValid =
    odometer !== '' && Number.isFinite(odoNum) && odoNum >= 0;
  const odoBelowLast = odoValid && lastOdometer != null && odoNum < lastOdometer;

  const handleSave = async () => {
    if (!odoValid || saving) return;
    if (odoBelowLast) {
      toast.error(
        `Kilometerstand kleiner als letzter Eintrag (${fmtKm(lastOdometer)})`
      );
      return;
    }
    setSaving(true);
    try {
      await addFuelEntry(log.id, {
        entryDate: date,
        odometer: odoNum,
        fuelCost: isFillUp ? Number(fuelCost) : null,
        liters: isFillUp && liters !== '' ? Number(liters) : null,
        note
      });
      onSaved();
    } catch (err) {
      toast.error(err.message || 'Speichern fehlgeschlagen');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-xl shadow-xl w-full sm:max-w-md p-5 max-h-[95vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Neuer Eintrag
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Datum
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="input w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Kilometerstand
            </label>
            <div className="relative">
              <input
                type="number"
                inputMode="numeric"
                value={odometer}
                onChange={(e) => setOdometer(e.target.value)}
                placeholder={
                  lastOdometer != null
                    ? `letzter: ${lastOdometer}`
                    : 'z.B. 124356'
                }
                className="input w-full pr-10"
                autoFocus
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                km
              </span>
            </div>
            {odoBelowLast && (
              <div className="mt-1 text-xs text-red-600 dark:text-red-400">
                Wert ist kleiner als der letzte gespeicherte Stand (
                {fmtKm(lastOdometer)}).
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              id="fillup-toggle"
              type="checkbox"
              checked={isFillUp}
              onChange={(e) => setIsFillUp(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
            />
            <label
              htmlFor="fillup-toggle"
              className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer flex items-center gap-1.5"
            >
              <Receipt size={14} className="text-primary-600" />
              Tankvorgang — Gesamtbetrag eintragen
            </label>
          </div>

          {isFillUp && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gesamtbetrag ({log.currency})
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={fuelCost}
                  onChange={(e) => setFuelCost(e.target.value)}
                  placeholder="z.B. 78.40"
                  className="input w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Liter (optional)
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  value={liters}
                  onChange={(e) => setLiters(e.target.value)}
                  placeholder="z.B. 42.30"
                  className="input w-full"
                />
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notiz (optional)
            </label>
            <input
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="z.B. Autobahn nach Hamburg"
              className="input w-full"
            />
          </div>

          <div className="flex gap-2 pt-2">
            <button
              onClick={handleSave}
              disabled={
                !odoValid || saving || (isFillUp && fuelCost === '')
              }
              className="btn bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex-1"
            >
              {saving ? 'Speichere…' : 'Speichern'}
            </button>
            <button
              onClick={onClose}
              className="btn-secondary"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// SettingsModal — members, invite link, rename, leave/delete.
// =============================================================================
const SettingsModal = ({ log, members, onClose, onChanged, onLeftOrDeleted }) => {
  const { user } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const prompt = usePrompt();

  const [inviteUrl, setInviteUrl] = useState(null);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!log.isOwner) return;
    setInviteLoading(true);
    getFuelLogInviteUrl(log.id)
      .then((url) => setInviteUrl(url))
      .catch(() => setInviteUrl(null))
      .finally(() => setInviteLoading(false));
  }, [log.id, log.isOwner]);

  const handleCopy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Konnte Link nicht kopieren');
    }
  };

  const handleRename = async () => {
    const next = await prompt({
      title: 'Liste umbenennen',
      defaultValue: log.name,
      confirmLabel: 'Speichern'
    });
    if (!next || next.trim() === log.name) return;
    try {
      await updateFuelLog(log.id, { name: next.trim() });
      await onChanged();
      toast.success('Umbenannt');
    } catch (err) {
      toast.error(err.message || 'Umbenennen fehlgeschlagen');
    }
  };

  const handleDelete = async () => {
    const ok = await confirm({
      title: 'Tank-Liste löschen?',
      message: `„${log.name}" wird mit allen Einträgen gelöscht. Das kann nicht rückgängig gemacht werden.`,
      confirmLabel: 'Endgültig löschen',
      destructive: true
    });
    if (!ok) return;
    try {
      await deleteFuelLog(log.id);
      await onLeftOrDeleted();
      toast.success('Liste gelöscht');
    } catch (err) {
      toast.error(err.message || 'Löschen fehlgeschlagen');
    }
  };

  const handleLeave = async () => {
    const ok = await confirm({
      title: 'Liste verlassen?',
      message: 'Du verlierst den Zugriff auf alle Einträge.',
      confirmLabel: 'Verlassen',
      destructive: true
    });
    if (!ok) return;
    try {
      await leaveFuelLog(log.id);
      await onLeftOrDeleted();
      toast.success('Liste verlassen');
    } catch (err) {
      toast.error(err.message || 'Verlassen fehlgeschlagen');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Einstellungen
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
              {log.name}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded"
          >
            <X size={18} />
          </button>
        </div>

        {/* Invite */}
        {log.isOwner && (
          <div className="mb-5">
            <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
              Einladungs-Link
            </div>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={inviteLoading ? 'Lade…' : inviteUrl || ''}
                className="input flex-1 text-xs font-mono"
                onFocus={(e) => e.target.select()}
              />
              <button
                onClick={handleCopy}
                disabled={!inviteUrl}
                className="btn-secondary !p-2"
                aria-label="Link kopieren"
              >
                <Copy size={16} />
              </button>
            </div>
            {copied && (
              <div className="mt-1 text-xs text-green-600 dark:text-green-400">
                Kopiert!
              </div>
            )}
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              Wer den Link öffnet und sich anmeldet, wird automatisch
              hinzugefügt.
            </p>
          </div>
        )}

        {/* Members */}
        <div className="mb-5">
          <div className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2 flex items-center gap-1">
            <UsersIcon size={12} /> Mitglieder
          </div>
          <ul className="divide-y divide-gray-200 dark:divide-gray-800 border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
            {members.map((m) => {
              const isMe = user && m.userId === user.id;
              return (
                <li
                  key={m.userId}
                  className="flex items-center gap-3 px-3 py-2.5 bg-white dark:bg-gray-900"
                >
                  <MemberAvatar member={m} size={36} />
                  <div className="flex-1 min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                    {m.displayName || m.userId.slice(0, 8)}
                    {isMe && (
                      <span className="ml-2 text-xs text-gray-400">(du)</span>
                    )}
                  </div>
                  <span
                    className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                      m.isOwner
                        ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
                        : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300'
                    }`}
                  >
                    {m.isOwner && <Crown size={12} />}
                    {m.isOwner ? 'Inhaber' : 'Mitglied'}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2">
          {log.isOwner ? (
            <>
              <button
                onClick={handleRename}
                className="btn-secondary w-full justify-start flex items-center gap-2"
              >
                <SettingsIcon size={14} />
                Liste umbenennen
              </button>
              <button
                onClick={handleDelete}
                className="btn w-full justify-start flex items-center gap-2 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800/50"
              >
                <Trash2 size={14} />
                Liste löschen
              </button>
            </>
          ) : (
            <button
              onClick={handleLeave}
              className="btn w-full justify-start flex items-center gap-2 bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50 border border-red-200 dark:border-red-800/50"
            >
              <LogOut size={14} />
              Liste verlassen
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FuelLog;
